import React, { ReactNode } from 'react';
import { Case } from 'services/ClaimscoreApiService/Cases/types';
import { Spacing } from '../../design-system/spacing';
import { CleanButton } from '../../utils/clean-button';
import { styled } from '../../utils/styled';
import { Button } from '../Button';
import { Stack } from '../Stack';
import { Typography } from '../Typography';

const ListContainer = styled('div')`
  border-radius: ${({ theme }) => theme.borderRadii.outer};
  background: ${({ theme }) => theme.palette.neutral[900]};
`;

const StyledButton = styled(CleanButton)<{ isRightIcon: boolean }>`
  cursor: ${({ isRightIcon }) => (isRightIcon ? 'default' : 'pointer')};
  overflow: hidden;
  :first-of-type {
    border-top-right-radius: ${({ theme }) => theme.borderRadii.outer};
    border-top-left-radius: ${({ theme }) => theme.borderRadii.outer};
  }

  :last-of-type {
    border-bottom-right-radius: ${({ theme }) => theme.borderRadii.outer};
    border-bottom-left-radius: ${({ theme }) => theme.borderRadii.outer};
  }
`;

const StyledTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'rightIcon',
})<{ rightIcon: boolean }>`
  max-width: ${({ rightIcon }) => (rightIcon ? '156px' : '216px')};
  margin-bottom: ${({ theme }) => theme.spacing(Spacing.Tiny)};
`;

const ItemWrapper = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'hasIcon',
})<{ hasIcon?: boolean }>`
  padding: ${({ theme }) => theme.spacing(Spacing.Medium)};
  &:hover {
    background: ${({ theme, hasIcon }) =>
      hasIcon ? undefined : theme.palette.neutral[800]};
  }
  flex-direction: row;
`;

export interface CaseDisplayProps {
  readonly cases: (Pick<Case, 'caseID' | 'caseName'> & {
    rightIcon?: ReactNode;
    onClick?: () => void;
  })[];
}

export function CaseDisplay({ cases }: CaseDisplayProps) {
  const renderContent = ({
    caseID,
    caseName,
    rightIcon,
    onClick,
  }: (typeof cases)[0]) => (
    <ItemWrapper hasIcon={Boolean(rightIcon)} key={caseID}>
      <Stack>
        <StyledTypography
          color="text.primaryInverted"
          noWrap
          rightIcon
          title={caseName}
          variant="subtitle"
        >
          {caseName}
        </StyledTypography>
      </Stack>
      {rightIcon ? (
        <Button
          ml="auto"
          onClick={onClick}
          startIcon={rightIcon}
          variant="text"
        />
      ) : null}
    </ItemWrapper>
  );

  return (
    <ListContainer>
      {cases.map(({ caseID, caseName, rightIcon, onClick }) =>
        rightIcon ? (
          renderContent({ caseID, caseName, rightIcon, onClick })
        ) : (
          <StyledButton
            isRightIcon={Boolean(rightIcon)}
            key={caseID}
            onClick={rightIcon ? undefined : onClick}
            type="button"
          >
            {renderContent({ caseID, caseName, rightIcon, onClick })}
          </StyledButton>
        ),
      )}
    </ListContainer>
  );
}
